/**
 * Power BI Reports Configuration
 *
 * This file contains helper functions and constants for Power BI reports.
 * All report/page configuration is now fetched from the backend via PowerBiConfigService.
 */

import { SystemPermissions } from '@/models/User';
import {
  powerBiConfigService,
  PowerBiReportPage,
  PowerBiReport,
} from '@/services/PowerBiConfigService';

// Re-export types from service for convenience
export type { PowerBiReportPage, PowerBiReport } from '@/services/PowerBiConfigService';

/**
 * Maps a permission key string from the database to the SystemPermissions enum.
 * Returns undefined if no permission is required (public page).
 */
export function mapPermissionKey(permissionKey: string | null): SystemPermissions | undefined {
  if (!permissionKey) {
    return undefined;
  }
  // The permission key is stored as the enum key name (e.g., "Reports_QuoteNeedsReport_ALL")
  return SystemPermissions[permissionKey as keyof typeof SystemPermissions];
}

/**
 * Constructs the embed URL for a specific report page.
 * Requires PowerBiConfigService.getConfig() to be called first.
 *
 * @param report - The report containing this page
 * @param page - The report page definition
 * @returns The full embed URL for the iframe
 */
export function getEmbedUrl(report: PowerBiReport, page: PowerBiReportPage): string {
  return powerBiConfigService.buildEmbedUrl(report.reportId, page.pageName);
}

/**
 * Constructs the direct Power BI URL for opening in a new tab.
 * Requires PowerBiConfigService.getConfig() to be called first.
 *
 * @param report - The report containing this page
 * @param page - The report page definition
 * @returns The direct Power BI URL
 */
export function getPowerBiUrl(report: PowerBiReport, page: PowerBiReportPage): string {
  return powerBiConfigService.buildDirectUrl(report.reportId, page.pageName);
}

/**
 * Groups pages by category for a given report.
 */
export function getPagesByCategory(
  pages: PowerBiReportPage[]
): Record<string, PowerBiReportPage[]> {
  return pages.reduce((acc, page) => {
    if (!acc[page.category]) {
      acc[page.category] = [];
    }
    acc[page.category].push(page);
    return acc;
  }, {} as Record<string, PowerBiReportPage[]>);
}

/**
 * Category display order for consistent UI rendering.
 */
export const categoryOrder = [
  'Overview',
  'Quotes',
  'Sales',
  'Analytics',
  'Customer',
  'Purchasing',
  'Shipping',
  'Inventory',
  'Relationships',
  'Finance',
  'Sales History',
];

/**
 * Category icons for UI display.
 */
export const categoryIcons: Record<string, string> = {
  Overview: 'mdi-view-dashboard-outline',
  Quotes: 'mdi-format-quote-open',
  Sales: 'mdi-point-of-sale',
  Analytics: 'mdi-chart-box-outline',
  Customer: 'mdi-account-multiple-outline',
  Purchasing: 'mdi-cart-outline',
  Shipping: 'mdi-truck-outline',
  Inventory: 'mdi-package-variant',
  Relationships: 'mdi-handshake-outline',
  Finance: 'mdi-finance',
  'Sales History': 'mdi-history',
};
